import { z } from "zod";
/**
 * Task import (WP 3.11): the mapping a person builds between a spreadsheet's
 * columns and a task's fields, the presets that build it for them, and the
 * value rules (dates, durations, statuses) the server applies to every cell.
 * Declared once so the dialog's guesses and the server's validation agree.
 */
export const IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const IMPORT_MAX_ROWS = 5000;
export const IMPORT_MAX_COLUMNS = 100;
/** How long after committing an import can still be undone. */
export const IMPORT_UNDO_HOURS = 24;
export const IMPORT_TARGETS = [
    "title",
    "description",
    "status",
    "priority",
    "dueDate",
    "startDate",
    "completed",
    "assignee",
    "tags",
    "section",
    "milestone",
    "estimate",
    "parent",
    "custom",
];
export const IMPORT_TARGET_LABEL = {
    title: "Title",
    description: "Description",
    status: "Status",
    priority: "Priority",
    dueDate: "Due date",
    startDate: "Start date",
    completed: "Completed (date or yes/no)",
    assignee: "Assignee (email or name)",
    tags: "Tags / labels",
    section: "Section / list",
    milestone: "Milestone",
    estimate: "Estimate",
    parent: "Parent task (makes a subtask)",
    custom: "Custom field",
};
/** Targets that take several columns (their values are combined); every other target takes one. */
const REPEATABLE = new Set(["assignee", "tags", "custom"]);
export const IMPORT_STATUSES = ["todo", "in_progress", "review", "done", "cancelled"];
export const IMPORT_PRIORITIES = ["low", "normal", "medium", "high", "urgent"];
export const importColumnMapping = z
    .object({
    /** Position of the column in the file's header row. Columns not listed are ignored. */
    column: z.number().int().min(0).max(IMPORT_MAX_COLUMNS - 1),
    target: z.enum(IMPORT_TARGETS),
    /** For `custom`: the project field the column fills. */
    fieldId: z.string().min(1).max(64).optional(),
})
    .strict();
export const importMappingBody = z
    .object({
    columns: z.array(importColumnMapping).max(IMPORT_MAX_COLUMNS),
    /** Source status value (compared case-insensitively) -> task status. Checked before the built-in synonyms. */
    statusMap: z.record(z.string().max(200), z.enum(IMPORT_STATUSES)).default({}),
    /** How "03/04/2026" reads. ISO dates and month names are never ambiguous. */
    dateOrder: z.enum(["dmy", "mdy"]).default("dmy"),
    /** What a bare number in an estimate column means ("2h" and "30m" always say). */
    estimateUnit: z.enum(["minutes", "hours"]).default("hours"),
})
    .strict()
    .superRefine((mapping, ctx) => {
    const seen = new Set();
    let title = false;
    for (const entry of mapping.columns) {
        if (entry.target === "title")
            title = true;
        if (entry.target === "custom") {
            if (!entry.fieldId)
                ctx.addIssue({ code: "custom", message: "Choose which custom field a column fills." });
            else if (seen.has(`custom:${entry.fieldId}`))
                ctx.addIssue({ code: "custom", message: "A custom field can only be filled by one column." });
            seen.add(`custom:${entry.fieldId}`);
        }
        else if (!REPEATABLE.has(entry.target)) {
            if (seen.has(entry.target))
                ctx.addIssue({ code: "custom", message: `${IMPORT_TARGET_LABEL[entry.target]} is mapped more than once.` });
            seen.add(entry.target);
        }
    }
    if (!title)
        ctx.addIssue({ code: "custom", message: "Map one column to Title." });
});
/* ─────────────────────────────── header detection ─────────────────────────────── */
/** "Section/Column" -> "sectioncolumn", "Due Date" -> "duedate". */
export function normalizeHeader(header) {
    return header
        .normalize("NFKD")
        .replace(/[̀-ͯ]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "");
}
/** Generic header names, tried in order; the first column present wins each target. */
const GENERIC_RULES = [
    { target: "title", headers: ["title", "name", "taskname", "task", "cardname", "summary", "subject"] },
    { target: "description", headers: ["description", "notes", "details", "carddescription", "body"] },
    { target: "status", headers: ["status", "state", "taskstatus"] },
    { target: "priority", headers: ["priority"] },
    { target: "dueDate", headers: ["duedate", "due", "deadline", "dueon", "finishdate", "enddate"] },
    { target: "startDate", headers: ["startdate", "start", "startson", "begindate"] },
    { target: "completed", headers: ["completedat", "completeddate", "completed", "completiondate", "duecomplete", "done"] },
    { target: "assignee", headers: ["assigneeemail", "assignee", "assignedto", "owner", "members", "member"] },
    { target: "tags", headers: ["tags", "labels", "label"] },
    { target: "section", headers: ["sectioncolumn", "section", "listname", "list", "column", "group", "bucket"] },
    { target: "milestone", headers: ["milestone"] },
    { target: "estimate", headers: ["estimate", "estimatedtime", "timeestimate", "estimatedhours", "originalestimate"] },
    { target: "parent", headers: ["parenttask", "parent"] },
];
/** Asana's CSV export: Task ID, Created At, Completed At, Last Modified, Name, Section/Column, Assignee, Assignee Email, Start Date, Due Date, Tags, Notes, Projects, Parent task, … */
const ASANA_RULES = [
    { target: "title", headers: ["name"] },
    { target: "description", headers: ["notes"] },
    // The email matches reliably; the display name is only a fallback.
    { target: "assignee", headers: ["assigneeemail", "assignee"] },
    { target: "dueDate", headers: ["duedate"] },
    { target: "startDate", headers: ["startdate"] },
    { target: "completed", headers: ["completedat"] },
    { target: "section", headers: ["sectioncolumn", "section"] },
    { target: "tags", headers: ["tags"] },
    { target: "parent", headers: ["parenttask"] },
    { target: "priority", headers: ["priority"] },
    { target: "estimate", headers: ["estimatedtime", "timeestimate"] },
];
/** The Trello board CSV exports: Card ID, Card Name, Card URL, Card Description, Labels, Members, Due Date, List Name, Due Complete, … */
const TRELLO_RULES = [
    { target: "title", headers: ["cardname"] },
    { target: "description", headers: ["carddescription"] },
    { target: "assignee", headers: ["members"] },
    { target: "dueDate", headers: ["duedate"] },
    { target: "startDate", headers: ["startdate"] },
    { target: "completed", headers: ["duecomplete"] },
    // A Trello list is a stage of work; here it becomes a section. A list that
    // is clearly "Done" / "Doing" also sets the status (see `statusFromSection`).
    { target: "section", headers: ["listname"] },
    { target: "tags", headers: ["labels"] },
];
export const IMPORT_PRESETS = [
    { id: "asana", label: "Asana CSV export", rules: ASANA_RULES },
    { id: "trello", label: "Trello CSV export", rules: TRELLO_RULES },
    { id: "generic", label: "Detect from column names", rules: GENERIC_RULES },
];
/** Which preset a header row looks like. Falls back to generic detection. */
export function detectPreset(headers) {
    const keys = new Set(headers.map(normalizeHeader));
    if (keys.has("cardname"))
        return "trello";
    if (keys.has("name") && keys.has("taskid") && (keys.has("assigneeemail") || keys.has("sectioncolumn") || keys.has("completedat") || keys.has("parenttask")))
        return "asana";
    return "generic";
}
/**
 * The mapping a preset suggests for these headers. Columns whose header names
 * one of the project's custom fields fill that field (whatever the preset), and
 * everything else is left unmapped (ignored) for the person to decide.
 */
export function suggestColumns(headers, presetId, fields = []) {
    const preset = IMPORT_PRESETS.find((p) => p.id === presetId) ?? IMPORT_PRESETS[2];
    const keys = headers.map(normalizeHeader);
    const used = new Set();
    const out = [];
    for (const rule of preset.rules) {
        for (const candidate of rule.headers) {
            const column = keys.findIndex((key, i) => key === candidate && !used.has(i));
            if (column < 0)
                continue;
            used.add(column);
            out.push({ column, target: rule.target });
            break;
        }
    }
    const byName = new Map(fields.map((f) => [normalizeHeader(f.name), f.id]));
    const taken = new Set();
    keys.forEach((key, column) => {
        const fieldId = byName.get(key);
        if (used.has(column) || !fieldId || taken.has(fieldId) || key === "")
            return;
        taken.add(fieldId);
        out.push({ column, target: "custom", fieldId });
    });
    return out.sort((a, b) => a.column - b.column);
}
const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
function validYmd(y, m, d) {
    if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2200)
        return false;
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}
const pad = (n) => String(n).padStart(2, "0");
/**
 * A cell as a date. Date-only values come back as `YYYY-MM-DDT00:00:00.000Z`
 * (how the app stores a due date everywhere); a value with a time and zone keeps
 * its instant, and one with a time and no zone is read as UTC. Returns `null`
 * for a blank cell.
 */
export function parseImportDate(raw, dateOrder = "dmy") {
    const text = raw.trim();
    if (text === "")
        return { ok: true, value: null };
    const fail = () => ({ ok: false, error: `Could not read "${text.slice(0, 40)}" as a date.` });
    const date = (y, m, d) => validYmd(y, m, d) ? { ok: true, value: `${y}-${pad(m)}-${pad(d)}T00:00:00.000Z` } : fail();
    // 2026-03-05, 2026/3/5
    let m = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(text);
    if (m)
        return date(Number(m[1]), Number(m[2]), Number(m[3]));
    // ISO with a time: 2026-03-05T14:30:00Z, 2026-03-05 14:30, with or without zone.
    m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2})?$/i.exec(text);
    if (m) {
        if (!validYmd(Number(m[1]), Number(m[2]), Number(m[3])))
            return fail();
        const zone = m[7] ? (m[7].toUpperCase() === "Z" ? "Z" : m[7].length === 5 ? `${m[7].slice(0, 3)}:${m[7].slice(3)}` : m[7]) : "Z";
        const t = new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6] ?? "00"}${zone}`);
        return Number.isNaN(t.getTime()) ? fail() : { ok: true, value: t.toISOString() };
    }
    // 05/03/2026, 5.3.2026, 5-3-26
    m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})(?:\s+\d{1,2}:\d{2}.*)?$/.exec(text);
    if (m) {
        const a = Number(m[1]);
        const b = Number(m[2]);
        let y = Number(m[3]);
        if (m[3].length === 2)
            y += 2000;
        // A value that can only be one order is read that way, whatever was chosen.
        const [day, month] = a > 12 ? [a, b] : b > 12 ? [b, a] : dateOrder === "dmy" ? [a, b] : [b, a];
        return date(y, month, day);
    }
    // Mar 5, 2026 · 5 Mar 2026 · March 5 2026 (optionally followed by a time, which is dropped)
    const lower = text.toLowerCase().replace(/,/g, " ").replace(/\s+/g, " ");
    m = /^([a-z]{3,9})\.? (\d{1,2})(?:st|nd|rd|th)? (\d{4})(?: .*)?$/.exec(lower);
    if (m) {
        const month = MONTHS.indexOf(m[1].slice(0, 3)) + 1;
        return month > 0 ? date(Number(m[3]), month, Number(m[2])) : fail();
    }
    m = /^(\d{1,2})(?:st|nd|rd|th)? ([a-z]{3,9})\.? (\d{4})(?: .*)?$/.exec(lower);
    if (m) {
        const month = MONTHS.indexOf(m[2].slice(0, 3)) + 1;
        return month > 0 ? date(Number(m[3]), month, Number(m[1])) : fail();
    }
    return fail();
}
/** "2h", "30m", "1h 30m", "1:30", "90 min", or a bare number in `unit`. Blank is no estimate. */
export function parseImportEstimate(raw, unit = "hours") {
    const text = raw.trim().toLowerCase();
    if (text === "")
        return { ok: true, value: null };
    const bad = () => ({ ok: false, error: `Could not read "${raw.trim().slice(0, 40)}" as a time estimate.` });
    const cap = (minutes) => Number.isFinite(minutes) && minutes >= 0 && minutes <= 100_000 ? { ok: true, value: Math.round(minutes) } : bad();
    const clock = /^(\d+):(\d{2})$/.exec(text);
    if (clock)
        return cap(Number(clock[1]) * 60 + Number(clock[2]));
    const plain = /^\d+(?:[.,]\d+)?$/.exec(text);
    if (plain) {
        const n = Number(text.replace(",", "."));
        return cap(unit === "hours" ? n * 60 : n);
    }
    // Units, in any order: 1d 2h 30m (a day is eight working hours, as elsewhere in the app).
    let minutes = 0;
    let matched = 0;
    const rest = text.replace(/(\d+(?:[.,]\d+)?)\s*(days?|d|hours?|hrs?|h|minutes?|mins?|m)(?![a-z])/g, (_all, n, u) => {
        const v = Number(n.replace(",", "."));
        minutes += u.startsWith("d") ? v * 480 : u.startsWith("h") ? v * 60 : v;
        matched += 1;
        return "";
    });
    if (matched === 0 || rest.replace(/[\s,]|and/g, "") !== "")
        return bad();
    return cap(minutes);
}
const STATUS_WORDS = {
    todo: "todo",
    "to do": "todo",
    "to-do": "todo",
    open: "todo",
    new: "todo",
    backlog: "todo",
    "not started": "todo",
    planned: "todo",
    "in progress": "in_progress",
    "in-progress": "in_progress",
    in_progress: "in_progress",
    doing: "in_progress",
    started: "in_progress",
    wip: "in_progress",
    active: "in_progress",
    ongoing: "in_progress",
    review: "review",
    "in review": "review",
    "under review": "review",
    testing: "review",
    qa: "review",
    done: "done",
    complete: "done",
    completed: "done",
    closed: "done",
    resolved: "done",
    finished: "done",
    cancelled: "cancelled",
    canceled: "cancelled",
    abandoned: "cancelled",
    "won't do": "cancelled",
    wontfix: "cancelled",
};
/** A status cell as a status, or undefined when neither the person's map nor the built-in words know it. */
export function parseImportStatus(raw, statusMap = {}) {
    const key = raw.trim().toLowerCase().replace(/\s+/g, " ");
    const mapped = Object.entries(statusMap).find(([source]) => source.trim().toLowerCase().replace(/\s+/g, " ") === key);
    return mapped?.[1] ?? STATUS_WORDS[key];
}
/**
 * Lists are free-form in Trello, but "Done" and "In Progress" are what most
 * boards call two of them: a section whose name is one of those words sets the
 * status, any other name says nothing.
 */
export function statusFromSection(name) {
    const key = name.trim().toLowerCase().replace(/\s+/g, " ");
    const status = STATUS_WORDS[key];
    return status && status !== "todo" ? status : undefined;
}
const PRIORITY_WORDS = {
    low: "low",
    lowest: "low",
    minor: "low",
    p3: "low",
    normal: "normal",
    none: "normal",
    medium: "medium",
    med: "medium",
    moderate: "medium",
    p2: "medium",
    high: "high",
    important: "high",
    p1: "high",
    urgent: "urgent",
    critical: "urgent",
    highest: "urgent",
    blocker: "urgent",
    p0: "urgent",
};
export function parseImportPriority(raw) {
    return PRIORITY_WORDS[raw.trim().toLowerCase()];
}
/** Whether a "completed" cell says yes: any date, or a true-ish word; blank and false-ish words say no. */
export function isCompletedCell(raw) {
    const text = raw.trim().toLowerCase();
    return text !== "" && !["false", "no", "n", "0", "-", "none", "open", "incomplete"].includes(text);
}
/** "a, b; c" -> ["a","b","c"], trimmed, de-duplicated (case-insensitively), blanks dropped. */
export function splitImportList(raw) {
    const seen = new Set();
    const out = [];
    for (const part of raw.split(/[,;\n|]+/)) {
        const item = part.trim();
        const key = item.toLowerCase();
        if (item === "" || seen.has(key))
            continue;
        seen.add(key);
        out.push(item);
    }
    return out;
}
//# sourceMappingURL=import.js.map